import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { MENU_SECTIONS } from '@/constant/ui'
import { useElementClick } from '@/hooks/HTMLElementClickHook'
import { isChristmas } from '@/helpers/DateHelper'

import {
  BottomWrapper,
  FooterBottomText,
  Main,
  MainWrapper,
  Menu,
  Secondary,
  LogoContainer,
  SectionLink,
  SectionTitle,
  SignupCta,
  SmallLinks,
  Social,
  Third,
  Mistletoe,
  PaymentMethods,
  CardIcon,
} from './ExozFooter.styles'
import Icon, { IconName } from './Icons'
import { PAYMENT_METHODS } from '@/constant/checkout'
import { useMemo } from 'react'
import { DEFAULT_LANGUAGE } from '@/helpers/UrlHelper'

const ExozFooter = () => {
  const privacyPolicyLinkTxt = 'Privacy'
  const instagramLinkTxt = 'Instagram'
  const youtubeLinkTxt = 'YouTube'
  const facebookLinkTxt = 'Facebook'

  const router = useRouter()

  const linkClick = useElementClick('Link')

  const paymentMethods: IconName[] = useMemo(
    () => PAYMENT_METHODS.map((method) => (method + 'Inverse') as IconName),
    []
  )

  return (
    <>
      <MainWrapper data-testid="exoz-footer">
        <Main>
          <LogoContainer>
            {isChristmas() && (
              <Mistletoe>
                <Icon name="mistletoe" />
              </Mistletoe>
            )}
            <Image src="/logos/exoz-logo-light-mono.svg" alt="Experience Oz" width={137} height={32} />
          </LogoContainer>
          <Menu>
            {MENU_SECTIONS.map(({ title, links }) => (
              <div key={title}>
                <SectionTitle>{title}</SectionTitle>
                {links.map(({ label, url, target }) => (
                  <SectionLink
                    data-testid={label}
                    href={url}
                    key={url}
                    target={target || '_self'}
                    onClick={(event) => linkClick({ location: `Footer | ${title}`, event })}
                  >
                    {label}
                  </SectionLink>
                ))}
              </div>
            ))}
            <Social>
              <Link
                href="https://www.facebook.com/ExperienceOz"
                target="_blank"
                data-testid={facebookLinkTxt}
                onClick={(event) => linkClick({ location: `Footer | ${facebookLinkTxt}`, event })}
                aria-label="Experience Oz Facebook"
              >
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.144531" width="43.214" height="43.214" rx="21.607" fill="#242424" />
                  <path
                    d="M22.4565 21.5H25.3125L25.6445 18.312H24.9795H22.7965L22.7955 17.188C22.7955 16.43 22.7195 16.188 23.5205 16.188H25.6455V13H23.5205C20.7225 13 19.2695 14.548 19.2695 16.918V17.172V18.313H17.1445V21.5H19.2695V30H19.3135H22.4565V24.687V21.569V21.5Z"
                    fill="white"
                  />
                </svg>
              </Link>
              <Link
                href="https://www.youtube.com/user/experienceoz01"
                target="_blank"
                data-testid={youtubeLinkTxt}
                onClick={(event) => linkClick({ location: `Footer | ${youtubeLinkTxt}`, event })}
                aria-label="Experience Oz Youtube"
              >
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.358398" width="43.214" height="43.214" rx="21.607" fill="#242424" />
                  <path
                    d="M31.1638 17.1028C31.0544 16.6956 30.8401 16.3243 30.5421 16.026C30.2442 15.7277 29.8731 15.5128 29.4661 15.403C27.9677 15 21.9615 15 21.9615 15C21.9615 15 15.9553 15 14.4569 15.4008C14.0497 15.5103 13.6784 15.725 13.3805 16.0234C13.0825 16.3218 12.8682 16.6933 12.7592 17.1007C12.3584 18.6012 12.3584 21.7307 12.3584 21.7307C12.3584 21.7307 12.3584 24.8603 12.7592 26.3587C12.98 27.1861 13.6317 27.8377 14.4569 28.0585C15.9553 28.4615 21.9615 28.4615 21.9615 28.4615C21.9615 28.4615 27.9677 28.4615 29.4661 28.0585C30.2935 27.8377 30.943 27.1861 31.1638 26.3587C31.5646 24.8603 31.5646 21.7307 31.5646 21.7307C31.5646 21.7307 31.5646 18.6012 31.1638 17.1028ZM20.0537 24.6031V18.8584L25.0268 21.7093L20.0537 24.6031Z"
                    fill="white"
                  />
                </svg>
              </Link>
              <Link
                href="https://www.instagram.com/experienceoz/"
                target="_blank"
                data-testid={instagramLinkTxt}
                onClick={(event) => linkClick({ location: `Footer | ${instagramLinkTxt}`, event })}
                aria-label="Experience Oz Instagram"
              >
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.572266" width="43.214" height="43.214" rx="21.607" fill="#242424" />
                  <path
                    d="M21.743 18.5153C20.1697 18.5153 18.8857 19.7992 18.8857 21.3726C18.8857 22.946 20.1697 24.23 21.743 24.23C23.3164 24.23 24.6004 22.946 24.6004 21.3726C24.6004 19.7992 23.3164 18.5153 21.743 18.5153ZM30.3129 21.3726C30.3129 20.1894 30.3237 19.0168 30.2572 17.8358C30.1908 16.4639 29.8778 15.2463 28.8746 14.2432C27.8693 13.2378 26.6539 12.927 25.282 12.8606C24.0988 12.7941 22.9263 12.8048 21.7452 12.8048C20.5619 12.8048 19.3894 12.7941 18.2083 12.8606C16.8364 12.927 15.6189 13.24 14.6157 14.2432C13.6104 15.2485 13.2996 16.4639 13.2331 17.8358C13.1667 19.019 13.1774 20.1915 13.1774 21.3726C13.1774 22.5537 13.1667 23.7284 13.2331 24.9095C13.2996 26.2813 13.6125 27.4989 14.6157 28.5021C15.621 29.5074 16.8364 29.8182 18.2083 29.8846C19.3915 29.9511 20.5641 29.9404 21.7452 29.9404C22.9284 29.9404 24.1009 29.9511 25.282 29.8846C26.6539 29.8182 27.8714 29.5052 28.8746 28.5021C29.8799 27.4967 30.1908 26.2813 30.2572 24.9095C30.3258 23.7284 30.3129 22.5558 30.3129 21.3726ZM21.743 25.769C19.3101 25.769 17.3466 23.8055 17.3466 21.3726C17.3466 18.9397 19.3101 16.9762 21.743 16.9762C24.176 16.9762 26.1394 18.9397 26.1394 21.3726C26.1394 23.8055 24.176 25.769 21.743 25.769ZM26.3195 17.8229C25.7515 17.8229 25.2927 17.3642 25.2927 16.7961C25.2927 16.2281 25.7515 15.7694 26.3195 15.7694C26.8875 15.7694 27.3463 16.2281 27.3463 16.7961C27.3464 16.931 27.32 17.0646 27.2685 17.1893C27.2169 17.3139 27.1413 17.4272 27.0459 17.5225C26.9505 17.6179 26.8373 17.6935 26.7126 17.7451C26.588 17.7966 26.4544 17.8231 26.3195 17.8229Z"
                    fill="white"
                  />
                </svg>
              </Link>
              {/* Disabling twitter as per direction from Mel */}
              {/* <Link
                href="https://twitter.com/Experience_Oz"
                target="_blank"
                data-testid={twitterLinkTxt}
                onClick={(event) => linkClick({ location: `Footer | ${twitterLinkTxt}`, event })}
              >
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.786133" width="43.214" height="43.214" rx="21.607" fill="#242424" />
                  <path
                    d="M31.3101 16.0829C30.6542 16.3658 29.9404 16.5694 29.2052 16.6487C29.9685 16.1953 30.5401 15.4788 30.8128 14.6338C30.0965 15.0599 29.3118 15.3587 28.4935 15.517C28.1515 15.1513 27.7378 14.86 27.2783 14.6613C26.8188 14.4625 26.3233 14.3605 25.8226 14.3616C23.797 14.3616 22.1679 16.0035 22.1679 18.0185C22.1679 18.3014 22.2022 18.5844 22.2579 18.8566C19.2248 18.698 16.5196 17.2489 14.7212 15.0304C14.3935 15.5901 14.2218 16.2274 14.2239 16.876C14.2239 18.1449 14.8691 19.2639 15.853 19.922C15.2732 19.8991 14.7069 19.7397 14.2003 19.4568V19.5018C14.2003 21.2788 15.4564 22.7514 17.1305 23.0901C16.8162 23.1718 16.4928 23.2135 16.1681 23.2144C15.9301 23.2144 15.7051 23.1909 15.4779 23.1587C15.9409 24.6078 17.2892 25.6602 18.8947 25.6945C17.6386 26.6784 16.0652 27.2572 14.3568 27.2572C14.0502 27.2572 13.7673 27.2465 13.4736 27.2122C15.0942 28.2518 17.0169 28.852 19.0876 28.852C25.8098 28.852 29.4881 23.283 29.4881 18.4493C29.4881 18.2907 29.4881 18.1321 29.4774 17.9735C30.189 17.4526 30.8128 16.8074 31.3101 16.0829Z"
                    fill="white"
                  />
                </svg>
              </Link> */}
              {/* TODO on click */}
              <SignupCta onClick={() => router.push(`/${router.query?.lang ?? DEFAULT_LANGUAGE}/signup`)}>
                <Icon name="australiaFilled" />
                <span>Pssst, there’s $10 waiting for you. Join for free today.</span>
              </SignupCta>
            </Social>
          </Menu>
        </Main>
        <Secondary>
          <SmallLinks>
            <span>&copy; Experience Oz</span>
            <span>
              <Link
                data-testid={privacyPolicyLinkTxt}
                href="/en/privacy-policy"
                onClick={(event) => linkClick({ location: `Footer | ${privacyPolicyLinkTxt}`, event })}
              >
                {privacyPolicyLinkTxt}
              </Link>
            </span>
            {/* TODO Uncomment when sitemap created */}
            {/*<span>*/}
            {/*  <Link*/}
            {/*    data-testid={sitemapLinkTxt}*/}
            {/*    href="/en/sitemap"*/}
            {/*    onClick={(event) => linkClick({ location: `Footer | ${sitemapLinkTxt}`, event })}*/}
            {/*  >*/}
            {/*    {sitemapLinkTxt}*/}
            {/*  </Link>*/}
            {/*</span>*/}
            <span style={{ cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Back to Top
            </span>
            <PaymentMethods>
              {paymentMethods.map((method) => (
                <CardIcon key={method}>
                  <Icon name={method} />
                </CardIcon>
              ))}
            </PaymentMethods>
          </SmallLinks>

          {/* Logo */}
          <Link href="https://www.thebigredgroup.com.au/" target="_blank">
            <Image
              src="/logos/PartofBigRedGroup-Logo-Primary-WithTagline-Horizontal-Reverse.png"
              alt="Big Red Group"
              width={293}
              height={32}
            />
          </Link>
        </Secondary>
      </MainWrapper>
      <BottomWrapper>
        <Third>
          <Image src="/footer-respects.png" alt="Experience Oz" width={32} height={32} />
          <FooterBottomText>
            We acknowledge and pay respect to the Traditional Custodians of Country and their connections and continuous
            care for the skies, lands and waterways throughout Australia.
          </FooterBottomText>
        </Third>
      </BottomWrapper>
    </>
  )
}

export default ExozFooter
